'use client';

import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';
import { PageTransition } from '@/components/layout/PageTransition';
import styles from '../../styles/contact.module.scss';

const SERVICES = [
  'Business Consultancy',
  'Funding Assistance',
  'Certification',
  'Legal Services',
  'Marketing',
  'IT Solutions',
];

function SearchableSelect({
  value,
  onChange,
  error,
}: {
  value: string;
  onChange: (v: string) => void;
  error?: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const filtered = SERVICES.filter(s =>
    s.toLowerCase().includes(search.toLowerCase()),
  );

  useEffect(() => {
    function onClickOut(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    }

    document.addEventListener('mousedown', onClickOut);
    return () => document.removeEventListener('mousedown', onClickOut);
  }, []);

  useEffect(() => {
    if (open) searchRef.current?.focus();
  }, [open]);

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={[
          'w-full flex items-center justify-between px-[11px] py-[9px]',
          'bg-white border rounded-[3px] cursor-pointer text-left',
          'font-[Arial,sans-serif] text-[0.87rem] transition-[border-color,box-shadow] duration-150',
          error ? 'border-[#C40000]' : 'border-[#b0bec5]',
          open ? styles.s2Open : '',
        ].join(' ')}
      >
        <span className={value ? 'text-[#0E1C2C]' : 'text-[#aab4bc] text-[0.83rem]'}>
          {value || 'Select a service'}
        </span>
        <span
          className={`text-[#6B7C8D] text-[12px] flex-shrink-0 transition-transform duration-150 ${open ? 'scale-y-[-1]' : ''}`}
        >
          {'\u25BE'}
        </span>
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute top-[calc(100%+3px)] left-0 right-0 bg-white border border-[#D5D9DD] border-t-[2px] border-t-[#E07030] rounded-b-[3px] shadow-[0_6px_20px_rgba(0,0,0,0.13)] z-[300]"
        >
          <div className="p-2 pb-[5px] border-b border-[#D5D9DD]">
            <input
              ref={searchRef}
              type="text"
              placeholder="Search service..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full box-border px-[10px] py-[7px] border border-[#D5D9DD] rounded-[3px] font-[Arial,sans-serif] text-[0.82rem] text-[#0E1C2C] outline-none focus:border-[#0D5FA3]"
            />
          </div>
          <ul className="list-none m-0 py-1 max-h-[190px] overflow-y-auto">
            {filtered.length === 0 && (
              <li className="px-[14px] py-3 text-[0.82rem] text-[#6B7C8D]">No results found</li>
            )}
            {filtered.map(s => (
              <li
                key={s}
                role="option"
                aria-selected={value === s}
                onMouseDown={() => {
                  onChange(s);
                  setOpen(false);
                  setSearch('');
                }}
                className={[
                  'px-[14px] py-[9px] text-[0.85rem] cursor-pointer transition-colors duration-100',
                  value === s
                    ? 'bg-[#fff7f2] text-[#C8601A] font-semibold'
                    : 'text-[#3D4F5E] hover:bg-[#E8F2FB] hover:text-[#0D5FA3]',
                ].join(' ')}
              >
                {value === s && <span className="text-[#C8601A]">{'\u2713 '}</span>}
                {s}
              </li>
            ))}
          </ul>
        </div>
      )}

      {error && <span className="text-[0.72rem] text-[#C40000] block mt-0.5">{error}</span>}
    </div>
  );
}

function Field({
  label,
  required,
  error,
  children,
}: {
  label?: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-[5px]">
      {label && (
        <label className="text-[0.77rem] font-semibold text-[#3D4F5E]">
          {label} {required && <span className="text-[#C8601A]">*</span>}
        </label>
      )}
      {children}
      {error && <span className="text-[0.72rem] text-[#C40000] block mt-0.5">{error}</span>}
    </div>
  );
}

function inputCls(hasError: boolean) {
  return [
    'font-[Arial,sans-serif] text-[0.87rem] text-[#0E1C2C]',
    'bg-white border rounded-[3px] px-[11px] py-[9px]',
    'outline-none w-full box-border transition-[border-color,box-shadow] duration-150',
    'placeholder:text-[#aab4bc] placeholder:text-[0.83rem]',
    hasError ? 'border-[#C40000]' : 'border-[#b0bec5]',
    styles.inputFocus,
  ].join(' ');
}

type FormState = {
  fullName: string;
  businessName: string;
  email: string;
  phone: string;
  service: string;
  message: string;
  consent: boolean;
};

const initialForm: FormState = {
  fullName: '',
  businessName: '',
  email: '',
  phone: '',
  service: '',
  message: '',
  consent: false,
};

export default function ContactPageNew() {
  const [form, setForm] = useState<FormState>(initialForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  function set(field: keyof FormState, val: string | boolean) {
    setForm(f => ({ ...f, [field]: val }));
    setErrors(e => ({ ...e, [field]: '' }));
  }

  function validate() {
    const e: Record<string, string> = {};

    if (!form.fullName.trim()) e.fullName = 'Please enter your full name.';
    else if (form.fullName.trim().length < 2) e.fullName = 'Name must be at least 2 characters.';

    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!form.email.trim()) e.email = 'Please enter your email address.';
    else if (!emailRe.test(form.email.trim())) e.email = 'Enter a valid email address.';

    const phoneRe = /^[+]?[\d\s\-(). ]{7,15}$/;
    if (!form.phone.trim()) e.phone = 'Please enter your phone number.';
    else if (!phoneRe.test(form.phone.trim())) e.phone = 'Enter a valid phone number.';

    if (!form.service) e.service = 'Please select a service.';

    if (!form.message.trim()) e.message = 'Please enter your message.';
    else if (form.message.trim().length < 10) e.message = 'Message must be at least 10 characters.';

    if (!form.consent) e.consent = 'You must agree to be contacted.';

    return e;
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();

    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    setStatus('loading');
    setErrorMsg('');

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fullName: form.fullName.trim(),
          businessName: form.businessName.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          serviceInterest: form.service,
          message: form.message.trim(),
          source: 'contact-form',
        }),
      });

      const payload = (await response.json()) as { success?: boolean; message?: string };

      if (!response.ok || !payload.success) {
        throw new Error(payload.message || 'Something went wrong. Please try again.');
      }

      setStatus('success');
      setForm(initialForm);
      setErrors({});
    } catch (err: unknown) {
      setStatus('error');
      setErrorMsg(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    }
  }

  return (
    <PageTransition>
      <main style={{ fontFamily: "Arial, 'Helvetica Neue', sans-serif" }}>
        <section className="relative h-[380px] overflow-hidden max-sm:h-[260px]">
          <div className="absolute inset-0">
            <Image
              src="https://picsum.photos/1920/560?random=12"
              alt=""
              fill
              className="object-cover"
              aria-hidden="true"
              unoptimized
            />
          </div>
          <div
            className="absolute inset-0"
            style={{ background: 'linear-gradient(to right,rgba(8,62,114,.84) 0%,rgba(8,62,114,.5) 55%,rgba(8,62,114,.25) 100%)' }}
          />
          <div className="absolute inset-0 flex flex-col justify-center px-16 max-sm:px-6">
            <p className="text-[0.68rem] font-bold tracking-[.24em] text-[#E07030] m-0 mb-3">
              FARSIGHT SOLUTIONS LLP
            </p>
            <h1
              className="font-bold text-white m-0 mb-2.5 tracking-tight"
              style={{ fontSize: 'clamp(2rem,4vw,3.2rem)' }}
            >
              Contact Us
            </h1>
            <p className="text-[1rem] text-white/70 m-0">We&apos;d love to hear from you</p>
          </div>
        </section>

        <div className="bg-[#F3F5F7] py-12 pb-[72px]">
          <div className="max-w-[1180px] mx-auto px-6 grid grid-cols-1 gap-7 lg:grid-cols-[1fr_370px] lg:gap-9">
            <div>
              <div className="bg-white border border-[#D5D9DD] rounded-[4px] shadow-[0_1px_4px_rgba(0,0,0,0.07)]">
                <div className="px-7 pt-[22px] pb-[18px] border-b border-[#D5D9DD]">
                  <h2 className="text-[1.2rem] font-bold text-[#0E1C2C] m-0 mb-1">Send Us a Message</h2>
                  <p className="text-[0.81rem] text-[#6B7C8D] m-0">Our team usually responds within one business day.</p>
                </div>

                {status === 'success' ? (
                  <div className="flex flex-col items-center gap-3.5 px-7 py-12 text-center">
                    <div className="w-[54px] h-[54px] rounded-full bg-[#167a40] text-white flex items-center justify-center text-[1.5rem] font-bold">
                      {'\u2713'}
                    </div>
                    <div>
                      <strong className="text-[1.05rem] text-[#0E1C2C] block mb-1">Message Received!</strong>
                      <p className="text-[0.84rem] text-[#6B7C8D] m-0">
                        Thank you for reaching out. A Farsight consultant will contact you shortly.
                      </p>
                    </div>
                    <button
                      onClick={() => setStatus('idle')}
                      className="mt-1.5 bg-transparent border border-[#D5D9DD] rounded-[3px] px-[22px] py-2 text-[0.82rem] text-[#0D5FA3] cursor-pointer font-[Arial,sans-serif] hover:bg-[#E8F2FB] transition-colors duration-150"
                    >
                      Send Another Message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} noValidate className="px-7 pt-[22px] pb-7 flex flex-col">
                    {status === 'error' && errorMsg && (
                      <div className="mb-4 rounded-[3px] border border-[#f0b8b8] bg-[#fff5f5] px-3 py-2 text-[0.79rem] text-[#9f1c1c]">
                        {errorMsg}
                      </div>
                    )}

                    <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                      <Field label="Full Name" required error={errors.fullName}>
                        <input
                          id="fullName"
                          type="text"
                          placeholder="Enter your full name"
                          value={form.fullName}
                          onChange={e => set('fullName', e.target.value)}
                          className={inputCls(!!errors.fullName)}
                        />
                      </Field>
                      <Field label="Business / Organisation">
                        <input
                          id="businessName"
                          type="text"
                          placeholder="Enter your business name"
                          value={form.businessName}
                          onChange={e => set('businessName', e.target.value)}
                          className={inputCls(false)}
                        />
                      </Field>
                    </div>

                    <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                      <Field label="Email Address" required error={errors.email}>
                        <input
                          id="email"
                          type="email"
                          placeholder="Enter your email address"
                          value={form.email}
                          onChange={e => set('email', e.target.value)}
                          className={inputCls(!!errors.email)}
                        />
                      </Field>
                      <Field label="Phone Number" required error={errors.phone}>
                        <input
                          id="phone"
                          type="tel"
                          placeholder="Enter your phone number"
                          value={form.phone}
                          onChange={e => set('phone', e.target.value)}
                          className={inputCls(!!errors.phone)}
                        />
                      </Field>
                    </div>

                    <div className="mb-4">
                      <Field label="Service of Interest" required error={errors.service}>
                        <SearchableSelect
                          value={form.service}
                          onChange={v => set('service', v)}
                          error={errors.service}
                        />
                      </Field>
                    </div>

                    <div className="mb-4">
                      <Field label="Message" required error={errors.message}>
                        <textarea
                          id="message"
                          rows={5}
                          placeholder="Enter your message or describe your requirements..."
                          value={form.message}
                          onChange={e => set('message', e.target.value)}
                          className={[inputCls(!!errors.message), styles.textareaResize].join(' ')}
                        />
                      </Field>
                    </div>

                    <div className="mb-4">
                      <label className="flex items-start gap-[9px] text-[0.79rem] text-[#6B7C8D] cursor-pointer leading-relaxed">
                        <input
                          type="checkbox"
                          checked={form.consent}
                          onChange={e => set('consent', e.target.checked)}
                          className={styles.checkboxAccent}
                        />
                        <span>
                          I agree to be contacted by Farsight Solutions regarding my inquiry.{' '}
                          <span className="text-[#C8601A]">*</span>
                        </span>
                      </label>
                      {errors.consent && (
                        <span className="text-[0.72rem] text-[#C40000] block mt-0.5">{errors.consent}</span>
                      )}
                    </div>

                    <button
                      type="submit"
                      disabled={status === 'loading'}
                      style={{ background: 'linear-gradient(to bottom,#f0a858,#E07030)' }}
                      className="w-full py-[11px] border border-[#c0580e] rounded-[3px] font-[Arial,sans-serif] text-[0.92rem] font-bold text-[#111] cursor-pointer mt-1.5 tracking-[.01em] transition-[filter,box-shadow] duration-150 hover:brightness-105 hover:shadow-[0_2px_8px_rgba(200,96,26,0.3)] disabled:opacity-60 disabled:cursor-default"
                    >
                      {status === 'loading' ? 'Sending...' : 'Send Message  ->'}
                    </button>
                  </form>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-5">
              <div className="bg-white border border-[#D5D9DD] border-t-[3px] border-t-[#C8601A] rounded-[4px] p-[22px] shadow-[0_1px_4px_rgba(0,0,0,0.07)]">
                <div className="flex items-center gap-2.5 mb-[18px]">
                  <span className="w-1 h-5 bg-[#C8601A] rounded-[2px] flex-shrink-0" />
                  <h3 className="m-0 text-[1rem] text-[#0E1C2C] font-bold">Contact Information</h3>
                </div>

                <div className="flex flex-col">
                  {[
                    {
                      icon: '\u{1F4CD}',
                      label: 'Registered Address',
                      content: (
                        <p className="m-0 text-[0.83rem] text-[#3D4F5E] leading-relaxed">
                          Ground Floor, Farsight Solutions LLP
                          <br />
                          ILD Business Center, H-207, Sector 63
                          <br />
                          Noida, Uttar Pradesh - 201009, India
                        </p>
                      ),
                    },
                    {
                      icon: '\u2709',
                      label: 'General Enquiries',
                      content: (
                        <p className="m-0 text-[0.83rem] text-[#3D4F5E] leading-relaxed">
                          <a href="mailto:Info@farsight.co.in" className="text-[#0D5FA3] no-underline hover:underline hover:text-[#C8601A]">
                            Info@farsight.co.in
                          </a>
                        </p>
                      ),
                    },
                    {
                      icon: '\u{1F464}',
                      label: 'Recruitment / HR',
                      content: (
                        <p className="m-0 text-[0.83rem] text-[#3D4F5E] leading-relaxed">
                          <a href="mailto:hr@farsight.co.in" className="text-[#0D5FA3] no-underline hover:underline hover:text-[#C8601A]">
                            hr@farsight.co.in
                          </a>
                        </p>
                      ),
                    },
                    {
                      icon: '\u{1F4DE}',
                      label: 'Phone',
                      content: (
                        <p className="m-0 text-[0.83rem] text-[#3D4F5E] leading-relaxed">
                          <a href="tel:+919821605930" className="text-[#0D5FA3] no-underline hover:underline hover:text-[#C8601A]">
                            +91 9821605930
                          </a>
                        </p>
                      ),
                    },
                  ].map((item, i, arr) => (
                    <div
                      key={item.label}
                      className={`flex gap-3 items-start py-[13px] ${i < arr.length - 1 ? 'border-b border-[#D5D9DD]' : ''}`}
                    >
                      <div className="text-[0.9rem] mt-px flex-shrink-0 w-5 text-center">{item.icon}</div>
                      <div>
                        <span className="text-[0.68rem] font-bold tracking-[.08em] uppercase text-[#6B7C8D] block mb-[3px]">
                          {item.label}
                        </span>
                        {item.content}
                      </div>
                    </div>
                  ))}
                </div>

                <a
                  href="https://wa.me/919821605930"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 flex items-center justify-center gap-2 bg-[#25D366] text-white rounded-[3px] px-4 py-[10px] text-[0.84rem] font-bold no-underline transition-[background,box-shadow] duration-150 hover:bg-[#1ebe5a] hover:shadow-[0_3px_10px_rgba(37,211,102,0.3)]"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                  Chat on WhatsApp
                </a>
              </div>

              <div className="bg-white border border-[#D5D9DD] rounded-[4px] overflow-hidden shadow-[0_1px_4px_rgba(0,0,0,0.07)]">
                <div className="px-4 py-[11px] border-b border-[#D5D9DD] flex items-center justify-between">
                  <span className="text-[0.68rem] font-bold tracking-[.08em] uppercase text-[#6B7C8D]">
                    Our Location
                  </span>
                  <a
                    href="https://maps.app.goo.gl/bNNRd2WDquq12S2t6"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[0.74rem] text-[#0D5FA3] no-underline font-semibold hover:underline"
                  >
                    {'Open in Maps ->'}
                  </a>
                </div>
                <iframe
                  title="Farsight Solutions Office"
                  src="https://maps.google.com/maps?q=28.6242943,77.3801475&z=15&output=embed"
                  width="100%"
                  height="260"
                  className="border-0 block"
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </div>
        </div>
      </main>
    </PageTransition>
  );
}
